import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import type { StrategySelectorItem } from '../components/StrategySelector';

const API_URL = import.meta.env.VITE_API_URL || '';

interface RawStrategy {
  name: string;
  fileName: string;
  description: string;
  type: string;
  content: string;
}

export const useStrategies = (type: 'active' | 'passive') => {
  const [strategies, setStrategies] = useState<StrategySelectorItem[]>([]);
  const [selectedStrategies, setSelectedStrategies] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const selectedSet = useMemo(() => new Set(selectedStrategies), [selectedStrategies]);

  const loadStrategies = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);

      const res = await fetch(`${API_URL}/api/strategies`, { signal: controller.signal });

      if (controller.signal.aborted) return;

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          const raw: RawStrategy[] = data.strategies[type] || [];
          const items: StrategySelectorItem[] = raw.map((s) => ({
            name: s.name,
            fileName: s.fileName,
            description: s.description,
            strategyKey: s.fileName.replace('.j2', ''),
          }));
          setStrategies(items);
          setSelectedStrategies(items.map((s) => s.strategyKey));
        }
      } else {
        throw new Error(`Failed to load strategies (${res.status})`);
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') return;
      const message = error instanceof Error ? error.message : 'Failed to load strategies';
      console.error(`Failed to load ${type} strategies:`, error);
      toast.error(message);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [type]);

  useEffect(() => {
    loadStrategies();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadStrategies]);

  const toggleStrategy = useCallback((strategyKey: string) => {
    setSelectedStrategies((prev) =>
      prev.includes(strategyKey) ? prev.filter((k) => k !== strategyKey) : [...prev, strategyKey],
    );
  }, []);

  const selectAll = useCallback(() => {
    setSelectedStrategies(strategies.map((s) => s.strategyKey));
  }, [strategies]);

  const deselectAll = useCallback(() => {
    setSelectedStrategies([]);
  }, []);

  return {
    strategies,
    selectedStrategies,
    selectedSet,
    loading,
    toggleStrategy,
    selectAll,
    deselectAll,
  };
};
